/**
 * Pagos / Liquidación de facturas de un crédito.
 *
 * Puerto de `Credito::payments` + `views/menu/credito/payments.php` y de
 * `API/Payments::credit_post`: la pantalla que el listado de créditos del panel
 * anterior abría con el botón del **billete** de cada fila (y desde el balance,
 * con «Agregar Pagos»). Es la que registra los **pagos de las facturas** que
 * salieron de un canje de vales.
 *
 * Qué se porta:
 * - La **posición del crédito** (línea, consumido, pagado, pendiente, disponible
 *   y por liquidar) y las **facturas por pagar** con su emisión, vencimiento,
 *   total, retención, abonado, pendiente, días vencidos y estado SUNAT.
 * - El **registro del pago**: método de pago (`abc_metodo_pago`), cuenta destino
 *   (`bank_accounts`), referencia, fecha e importe. El importe se reparte entre
 *   las facturas marcadas y, si sobra, sigue con las demás pendientes; se anota
 *   el movimiento en `movements_account` con el **crédito asociado**
 *   (`movement_credit`, que es lo que alimenta los «Pagos» de la lista y los
 *   «Abonos» del balance) y su reparto en `movement_detail`.
 * - Los **pagos ya registrados** del crédito con su reparto por factura.
 *
 * - El **archivo del comprobante** del pago: el original lo subía a
 *   `uploads/creditos/pagos/` y guardaba el nombre en `movement_file`
 *   (`Payments::credit_post`). Aquí viaja en base64 dentro del mismo JSON del
 *   pago —la API no usa `multipart`— y es la API la que escribe el archivo.
 */

import { BadgeDollarSign, Info, Paperclip, Receipt } from 'lucide-react';
import { useRef, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';
import { ApiError } from '@/lib/http/api-error';

import { usePagosCredito, useRegistrarPagoCredito } from '../cierre.api';
import type { FiltrosPagos } from '../cierre-gestion.types';
import { fechaCorta, hoyIso, monto } from '../gestion-formato';

const INICIO_POR_DEFECTO = '2022-09-02';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface Props {
  /** Crédito abierto (`null` cierra el diálogo). */
  id: number | null;
  onClose: () => void;
  /** Aviso de éxito que muestra la pantalla. */
  onAviso: (mensaje: string) => void;
}

export function CreditoPagoDialog({ id, onClose, onAviso }: Props) {
  const [filtros, setFiltros] = useState<FiltrosPagos>(() => ({
    desde: INICIO_POR_DEFECTO,
    hasta: hoyIso(),
  }));
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [metodo, setMetodo] = useState(0);
  const [cuenta, setCuenta] = useState(0);
  const [referencia, setReferencia] = useState('');
  const [fecha, setFecha] = useState(() => hoyIso());
  const [importe, setImporte] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  /** Comprobante del pago (`{ nombre, contenido_base64 }`) pendiente de enviar. */
  const [comprobante, setComprobante] = useState<{ nombre: string; contenido_base64: string } | null>(
    null,
  );

  const consulta = usePagosCredito(id, filtros);
  const registrar = useRegistrarPagoCredito(id);

  // Clave de un solo envío del pago en curso: si el envío se repite (timeout de red), el API
  // devuelve el pago ya registrado en lugar de aplicarlo dos veces. Se renueva al confirmar.
  const claveDeEnvio = useRef(nuevaClaveDeEnvio());

  const facturas = consulta.data?.facturas ?? [];
  const datos = consulta.data;

  // Al llegar (o cambiar) el listado se limpia la selección y el importe.
  const firma = `${id ?? 0}|${filtros.desde ?? ''}|${filtros.hasta ?? ''}|${facturas.map((factura) => factura.id).join(',')}`;
  const [firmaVista, setFirmaVista] = useState('');

  if (firma !== firmaVista) {
    setFirmaVista(firma);
    setSeleccion(new Set());
    setImporte('');
    setError(null);
    setComprobante(null);
  }

  const escogidas = facturas.filter((factura) => seleccion.has(factura.id));
  const pendienteEscogido = escogidas.reduce((suma, factura) => suma + factura.saldo, 0);

  /** Marca o desmarca una factura y ajusta el importe al pendiente elegido. */
  const alternar = (facturaId: number, marcado: boolean) => {
    setSeleccion((previo) => {
      const copia = new Set(previo);

      if (marcado) copia.add(facturaId);
      else copia.delete(facturaId);

      const total = facturas
        .filter((factura) => copia.has(factura.id))
        .reduce((suma, factura) => suma + factura.saldo, 0);

      setImporte(total > 0 ? total.toFixed(2) : '');

      return copia;
    });
  };

  /** Lee el archivo elegido y lo deja listo para enviar en base64. */
  const leerComprobante = (archivo: File | undefined) => {
    setError(null);

    if (!archivo) {
      setComprobante(null);

      return;
    }

    const permitidos = ['pdf', 'jpg', 'jpeg', 'png'];
    const extension = archivo.name.split('.').pop()?.toLowerCase() ?? '';

    if (!permitidos.includes(extension)) {
      setError('El comprobante debe ser un archivo PDF, JPG o PNG.');
      setComprobante(null);

      return;
    }

    if (archivo.size > 5 * 1024 * 1024) {
      setError('El comprobante del pago no debe superar los 5 MB.');
      setComprobante(null);

      return;
    }

    const lector = new FileReader();

    lector.onload = () =>
      setComprobante({
        nombre: archivo.name,
        contenido_base64: String(lector.result ?? ''),
      });
    lector.onerror = () => setError('No se pudo leer el comprobante elegido.');
    lector.readAsDataURL(archivo);
  };

  const registrarPago = () => {
    if (id === null) return;

    setError(null);
    registrar.mutate(
      {
        monto: Number(importe),
        metodo_pago_id: metodo,
        cuenta_id: cuenta,
        referencia: referencia.trim() || undefined,
        fecha_pago: fecha,
        facturas: [...seleccion],
        archivo: comprobante ?? undefined,
        clave_idempotencia: claveDeEnvio.current,
      },
      {
        onSuccess: (respuesta) => {
          claveDeEnvio.current = nuevaClaveDeEnvio();
          setConfirmar(false);
          setComprobante(null);
          onAviso(respuesta.mensaje);
        },
        onError: (fallo) => {
          setConfirmar(false);
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el pago.');
        },
      },
    );
  };

  const puedeRegistrar = Number(importe) > 0 && metodo > 0 && cuenta > 0;

  return (
    <>
      <Dialog
        open={id !== null}
        onClose={onClose}
        size="xl"
        title={datos ? `Pagos · ${datos.credito.cliente}` : 'Pagos / Liquidación de facturas'}
        description={
          datos
            ? `${datos.credito.numero_doc} · ${datos.credito.producto} · estado del crédito ${datos.credito.estado_pago ?? '—'}`
            : undefined
        }
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cerrar
            </Button>
            <Button
              size="sm"
              disabled={!puedeRegistrar}
              leftIcon={<BadgeDollarSign className="size-4" />}
              onClick={() => setConfirmar(true)}
            >
              Registrar pago
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Desde"
              type="date"
              value={filtros.desde ?? ''}
              onChange={(evento) => setFiltros((previo) => ({ ...previo, desde: evento.target.value }))}
            />
            <Input
              label="Hasta"
              type="date"
              value={filtros.hasta ?? ''}
              onChange={(evento) => setFiltros((previo) => ({ ...previo, hasta: evento.target.value }))}
            />
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={id === null || consulta.isFetching}
                onClick={() => void consulta.refetch()}
              >
                Consultar
              </Button>
            </div>
          </div>

          {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron leer los pagos">
              {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
            </Alert>
          ) : null}

          {datos ? (
            <>
              <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {[
                  ['Línea', datos.posicion.linea],
                  ['Consumido', datos.posicion.consumido],
                  ['Pagado', datos.posicion.pagado],
                  ['Pendiente', datos.posicion.pendiente],
                  ['Disponible', datos.posicion.disponible],
                  ['Por liquidar', datos.posicion.por_liquidar],
                ].map(([etiqueta, valor]) => (
                  <div key={String(etiqueta)} className="rounded-card border border-ink-100 bg-white p-3">
                    <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      {etiqueta}
                    </p>
                    <p className="text-sm font-semibold text-ink-800">{monto(Number(valor))}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-ink-500">
                  Facturas por pagar: {datos.totales.facturas} · total {monto(datos.totales.total)} ·
                  abonado {monto(datos.totales.abonado)} · <b>pendiente {monto(datos.totales.pendiente)}</b>
                  {datos.totales.vencido > 0 ? ` · vencido ${monto(datos.totales.vencido)}` : ''}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!facturas.length}
                    onClick={() => {
                      setSeleccion(new Set(facturas.map((factura) => factura.id)));
                      setImporte(
                        facturas.reduce((suma, factura) => suma + factura.saldo, 0).toFixed(2),
                      );
                    }}
                  >
                    Seleccionar todas
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!seleccion.size}
                    onClick={() => {
                      setSeleccion(new Set());
                      setImporte('');
                    }}
                  >
                    Limpiar
                  </Button>
                </div>
              </div>

              <div className="max-h-[34vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-3 py-2">
                        <input
                          type="checkbox"
                          className="size-4 accent-brand-500"
                          aria-label="Seleccionar todas las facturas"
                          checked={facturas.length > 0 && seleccion.size === facturas.length}
                          onChange={(evento) => {
                            if (evento.target.checked) {
                              setSeleccion(new Set(facturas.map((factura) => factura.id)));
                              setImporte(
                                facturas.reduce((suma, factura) => suma + factura.saldo, 0).toFixed(2),
                              );
                            } else {
                              setSeleccion(new Set());
                              setImporte('');
                            }
                          }}
                        />
                      </th>
                      <th className="px-3 py-2 whitespace-nowrap">Factura</th>
                      <th className="px-3 py-2 whitespace-nowrap">Emisión / vence</th>
                      <th className="px-3 py-2">Información</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Abonado</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Pendiente</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Vencidos</th>
                      <th className="px-3 py-2 whitespace-nowrap">SUNAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturas.map((factura) => (
                      <tr
                        key={factura.id}
                        className={
                          seleccion.has(factura.id)
                            ? 'border-t border-ink-100 bg-brand-50/40'
                            : 'border-t border-ink-100'
                        }
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className="size-4 accent-brand-500"
                            aria-label={`Incluir la factura ${factura.comprobante}`}
                            checked={seleccion.has(factura.id)}
                            onChange={(evento) => alternar(factura.id, evento.target.checked)}
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{factura.comprobante}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="block">{fechaCorta(factura.fecha_emision)}</span>
                          <span className="block text-xs text-ink-500">
                            {fechaCorta(factura.fecha_vencimiento)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="line-clamp-2" title={factura.informacion ?? undefined}>
                            {factura.informacion ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{monto(factura.total)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{monto(factura.abonado)}</td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap text-ink-800">
                          {monto(factura.saldo)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {factura.dias_vencidos > 0 ? (
                            <Badge variant="danger">{factura.dias_vencidos} días</Badge>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{factura.estado ?? '—'}</td>
                      </tr>
                    ))}

                    {facturas.length === 0 ? (
                      <tr>
                        <td className="px-3 py-6 text-center text-ink-500" colSpan={9}>
                          El crédito no tiene facturas pendientes de pago en el rango.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                  <tfoot className="bg-ink-50/60">
                    <tr className="border-t border-ink-100 font-semibold text-ink-800">
                      <td className="px-3 py-2" colSpan={6}>
                        Seleccionado / pendiente del crédito
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {monto(pendienteEscogido)} / {monto(datos.totales.pendiente)}
                      </td>
                      <td className="px-3 py-2" colSpan={2}>
                        {escogidas.length} de {datos.totales.facturas} facturas
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <section className="rounded-card border border-ink-100 bg-white p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <BadgeDollarSign className="size-4 text-brand-500" aria-hidden="true" />
                  Registrar pago
                </h3>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Método de pago</span>
                    <Select
                      className={CONTROL}
                      value={metodo}
                      onChange={(evento) => setMetodo(Number(evento.target.value))}
                    >
                      <option value={0}>SELECCIONE</option>
                      {datos.metodos.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.nombre}
                        </option>
                      ))}
                    </Select>
                  </label>

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Destino</span>
                    <Select
                      className={CONTROL}
                      value={cuenta}
                      onChange={(evento) => setCuenta(Number(evento.target.value))}
                    >
                      <option value={0}>SELECCIONE</option>
                      {datos.cuentas.map((item) => (
                        <option key={item.id} value={item.id}>
                          {[item.descripcion, item.numero].filter(Boolean).join(' - ')}
                        </option>
                      ))}
                    </Select>
                  </label>

                  <Input
                    label="Referencia y/o Nro. de operación"
                    placeholder="Opcional"
                    value={referencia}
                    onChange={(evento) => setReferencia(evento.target.value)}
                  />

                  <Input
                    label="Fecha del pago"
                    type="date"
                    max={hoyIso()}
                    value={fecha}
                    onChange={(evento) => setFecha(evento.target.value)}
                  />

                  <Input
                    label="Importe a pagar (S/)"
                    type="number"
                    min={0}
                    step="0.01"
                    value={importe}
                    onChange={(evento) => setImporte(evento.target.value)}
                  />

                  <div className="flex items-end">
                    <p className="text-xs text-ink-500">
                      El importe se reparte entre las facturas marcadas y, si sobra, sigue con las
                      demás pendientes; las que se cubren quedan pagadas por completo.
                    </p>
                  </div>

                  <ArchivoInput
                    className="sm:col-span-2 lg:col-span-3"
                    label="Comprobante del pago (opcional)"
                    accept=".pdf,.jpg,.jpeg,.png"
                    nombre={comprobante?.nombre ?? null}
                    hint={
                      comprobante
                        ? 'Se guardará en uploads/creditos/pagos/ y se anotará en movement_file.'
                        : 'PDF, JPG o PNG de hasta 5 MB.'
                    }
                    onSeleccionar={(elegido) => leerComprobante(elegido?.archivo)}
                  />
                </div>
              </section>

              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <Receipt className="size-4 text-brand-500" aria-hidden="true" />
                  Pagos registrados ({datos.pagos.length})
                </h3>

                {datos.pagos.length === 0 ? (
                  <p className="text-sm text-ink-500">Todavía no hay pagos registrados para este crédito.</p>
                ) : (
                  <ul className="divide-y divide-ink-100 overflow-hidden rounded-card border border-ink-100 bg-white">
                    {datos.pagos.map((pago) => (
                      <li key={pago.id} className="px-3 py-2 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium text-ink-800">
                            {fechaCorta(pago.fecha)} · {monto(pago.monto)}
                            {pago.referencia ? ` · Ref. ${pago.referencia}` : ''}
                          </span>
                          <span className="text-xs text-ink-500">
                            {[pago.metodo, pago.cuenta, pago.registrado_por]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </div>
                        {pago.reparto.length ? (
                          <p className="mt-0.5 text-xs text-ink-500">
                            Reparto: {pago.reparto.map((item) => `${item.factura} S/ ${monto(item.monto)}`).join(' · ')}
                          </p>
                        ) : null}
                        {pago.archivo ? (
                          <a
                            className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline"
                            href={`/uploads/creditos/pagos/${pago.archivo}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Paperclip className="size-3.5" aria-hidden="true" />
                            Comprobante {pago.archivo}
                          </a>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <Alert variant="info" title="Comprobante del pago">
                <span className={ICONO_EN_ALERTA}>
                  <Paperclip className="size-4" aria-hidden="true" />
                  El archivo elegido se guarda en `uploads/creditos/pagos/` y su nombre queda en
                  `movement_file`, igual que cuando lo subía el panel anterior.
                </span>
              </Alert>

              <Alert variant="info" title="De dónde salen los pagos de la lista">
                <span className={ICONO_EN_ALERTA}>
                  <Info className="size-4" aria-hidden="true" />
                  Cada pago se anota en `movements_account` con el crédito asociado (`movement_credit`),
                  así que aparecen en la columna «Pagos» del listado y en los «Abonos» del balance.
                </span>
              </Alert>
            </>
          ) : null}
        </div>
      </Dialog>

      <ConfirmDialog
        open={confirmar}
        title="Registrar el pago"
        description={`Se registrará un pago de S/ ${monto(Number(importe))} con ${
          escogidas.length || 'reparto automático entre'
        } ${escogidas.length ? 'factura(s) seleccionada(s)' : 'las facturas pendientes'} y ${metodo ? 'el método elegido' : 'un método'}.`}
        confirmLabel="Registrar"
        tone="primary"
        loading={registrar.isPending}
        onClose={() => setConfirmar(false)}
        onConfirm={registrarPago}
      >
        {error ? (
          <Alert variant="error" title="No se pudo registrar">
            {error}
          </Alert>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
